// @ts-nocheck
import React, { FC, HTMLAttributes, useState } from 'react';
import { styled } from '@mui/material/styles';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  TextField,
  FormHelperText,
  Typography,
  SvgIcon,
  Grid,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { LoadingButton } from '@mui/lab';
import { useDispatch } from 'src/store';
import { loginWithCpfCnpjAndEmail } from '../../../services/authService';
import { loginContato } from '../../../slices/account';
import { UserContatoDTO } from '../../../types/dtos/auth';

const PREFIX = 'CodeVerificationForm';

const classes = {
  root: `${PREFIX}-root`,
  arrowButton: `${PREFIX}-arrowButton`,
};

const StyledForm = styled('form')(() => ({
  [`& .${classes.root}`]: {
    display: 'flex',
    alignItems: 'center',
  },

  [`& .${classes.arrowButton}`]: {
    minWidth: '28px',
    padding: '6px',
  },
}));

interface Props extends HTMLAttributes<HTMLFormElement> {
  onSubmitConfirm: () => void;
  setIsCodeVerificationOpen: (isOpen: boolean) => void;
  user: UserContatoDTO;
  setUser: (user: UserContatoDTO) => void;
}

export const CodeVerificationForm: FC<Props> = ({
  className,
  onSubmitConfirm,
  setIsCodeVerificationOpen,
  user,
  setUser,
  ...rest
}) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [isResetting, setIsResetting] = useState(false);

  return (
    <Formik
      initialValues={{
        codeVerification: '',
      }}
      validationSchema={Yup.object().shape({
        codeVerification: Yup.string().required(
          'Código de verificação é obrigatório'
        ),
      })}
      onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
        try {
          await dispatch(
            loginContato(
              user.Portal,
              user.CodigoPessoa,
              user.Sequencia,
              values.codeVerification
            )
          );

          onSubmitConfirm();
        } catch (error) {
          let message;

          if (error instanceof Error) message = error.message;
          else message = String(error);

          if (!message) message = 'Conexão inválida com o servidor';

          setStatus({ success: false });
          setErrors({ submit: message });
          setSubmitting(false);
        }
      }}
      onReset={async (_, { setErrors, setStatus, setSubmitting }) => {
        try {
          setIsResetting(true);
          setErrors({ submit: '' });

          const newUser = await loginWithCpfCnpjAndEmail(
            user.Portal,
            user.CpfCnpj,
            user.Email
          );

          setUser({ ...newUser[0], CpfCnpj: user.CpfCnpj, Email: user.Email });

          enqueueSnackbar('Novo código enviado.', {
            variant: 'success',
          });

          setIsResetting(false);
        } catch (error) {
          let message;

          if (error instanceof Error) message = error.message;
          else message = String(error);

          if (!message) message = 'Conexão inválida com o servidor';

          setStatus({ success: false });
          setErrors({ submit: message });
          setSubmitting(false);
        }
      }}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        handleReset,
        isSubmitting,
        touched,
        values,
      }) => (
        <StyledForm noValidate onSubmit={handleSubmit} {...rest}>
          <Box className={classes.root} mb={2}>
            <Button
              onClick={() => setIsCodeVerificationOpen(false)}
              className={classes.arrowButton}
            >
              <SvgIcon fontSize="small">
                <ArrowBackIcon />
              </SvgIcon>
            </Button>
            <Typography variant="h4" color="textPrimary">
              Código de verificação
            </Typography>
          </Box>
          <Typography variant="body2" color="inherit">
            Um e-mail com o código de verificação foi enviado para{' '}
            <b>{user.Email}</b>
          </Typography>
          <Box mt={2} mb={2}>
            <TextField
              error={Boolean(
                touched.codeVerification && errors.codeVerification
              )}
              fullWidth
              autoFocus
              helperText={touched.codeVerification && errors.codeVerification}
              label="Código de verificação"
              margin="normal"
              name="codeVerification"
              onBlur={handleBlur}
              onChange={handleChange}
              type="text"
              value={values.codeVerification}
            />
          </Box>
          <Grid container spacing={1} alignItems="center" direction="row">
            <Grid item md={12} sm={12} xs={12}>
              <LoadingButton
                disabled={isResetting}
                loading={isSubmitting}
                fullWidth
                size="large"
                type="submit"
                variant="contained"
              >
                Confirmar
              </LoadingButton>
            </Grid>
            <Grid item md={12} sm={12} xs={12}>
              <LoadingButton
                color="secondary"
                disabled={isSubmitting}
                loading={isResetting}
                fullWidth
                size="large"
                variant="outlined"
                onClick={handleReset}
              >
                Enviar novo código
              </LoadingButton>
            </Grid>
            {errors.submit && (
              <Box mt={3}>
                <FormHelperText error>{errors.submit}</FormHelperText>
              </Box>
            )}
          </Grid>
        </StyledForm>
      )}
    </Formik>
  );
};
